import { FC, useCallback, useState, useEffect } from 'react';
import { FaExclamationTriangle, FaAt } from 'react-icons/fa';

import Modal from '@components/Modal';
import Input from '@components/Input';
import Button from '@components/Button';

import LoaderImage from '@assets/loader.gif';

import { forgotPasswordRequest } from '@api/requests';

import './ForgotPasswordModal.scss';

const emailRE =
  /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

const inputStyles = {
  marginBottom: 15,
};

const buttonStyles = {
  marginBottom: 20,
  marginTop: 20,
};

type ForgotPasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ForgotPasswordModal: FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [email, setEmail] = useState<string>('');
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSucceeded, setIsSucceeded] = useState<boolean>(false);

  const handleEmailChange = useCallback(v => {
    setEmailErrorMessage('');
    setErrorMessage('');
    setEmail(v.target.value);
  }, []);

  const handleSubmit = useCallback(async () => {
    let error = false;

    if (email.trim() && !emailRE.test(email)) {
      setEmailErrorMessage('email is invalid.');
      error = true;
    }

    if (!email.trim()) {
      setEmailErrorMessage('email is required.');
      error = true;
    }

    if (error) return;

    setIsLoading(true);

    const [err] = await forgotPasswordRequest(email);
    setIsLoading(false);

    if (err) {
      console.log(err);
      const msg =
        'something went wrong. Please refresh the page and try again.';
      setErrorMessage(msg);
    } else {
      setIsSucceeded(true);
    }
  }, [email]);

  useEffect(() => {
    setEmailErrorMessage('');
    setErrorMessage('');
    setIsSucceeded(false);
    setEmail('');
  }, [isOpen]);

  return (
    <>
      <Modal
        opened={isOpen && !isSucceeded}
        title="Forgot your password?"
        onClose={onClose}
        invertedColors
        crossButton
        footer={false}
        style={{ width: 600 }}
      >
        <div className="forgot-passowrd-modal">
          <p>We'll email you a link to reset your password</p>
          <Input
            data-id="userName"
            placeholder="Email"
            icon={<FaAt />}
            value={email}
            onChange={handleEmailChange}
            style={inputStyles}
            error={!!emailErrorMessage}
          />
          {(errorMessage || emailErrorMessage) && (
            <div className="login-modal__error-message">
              <FaExclamationTriangle />
              <span>
                {errorMessage}
                {emailErrorMessage}
              </span>
            </div>
          )}
          <Button
            label="Submit"
            disabled={isLoading}
            onClick={handleSubmit}
            style={buttonStyles}
            invertedColors
          />
          {isLoading && (
            <div className="forgot-passowrd-modal__loading-image">
              <img src={LoaderImage} alt="" style={{maxWidth: "40px"}} />
            </div>
          )}
        </div>
      </Modal>
      <Modal
        title="REQUEST SENT"
        opened={isOpen && isSucceeded}
        invertedColors
        crossButton
        onClose={onClose}
        style={{ width: 600, overflow: 'initial' }}
      >
        <div className="forgot-passowrd-modal__succeeded" style={{color:"#f0f8ff"}}>
          <h3>
            <span>Please check your email for further instructions.</span>
          </h3>
        </div>
      </Modal>
    </>
  );
};

export default ForgotPasswordModal;
